import type { AssetChart } from "@/schemas";

export interface DayChangeAsset {
  previous_close?: number;
  previous_close_at?: string;
  regular_market_open?: number;
}

function toUtcDayKey(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString().slice(0, 10);
}

function toTimestampMs(value: string): number | null {
  const date = new Date(value);
  const timestamp = date.getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return timestamp;
}

function getLastClosedDailyDayKey(chartData: AssetChart | null): string | null {
  const points = chartData?.price_points;
  if (!points?.length) {
    return null;
  }

  const referencePoint = points.length >= 2 ? points[points.length - 2] : points[points.length - 1];
  return toUtcDayKey(referencePoint.timestamp);
}

export function getValidatedPreviousClose(
  asset: DayChangeAsset | null,
  chartData: AssetChart | null
): number | null {
  if (!asset) {
    return null;
  }

  const previousClose = asset.previous_close;
  if (!previousClose || previousClose <= 0) {
    return null;
  }

  const previousCloseDayKey = asset.previous_close_at
    ? toUtcDayKey(asset.previous_close_at)
    : null;
  const lastClosedDailyDayKey = getLastClosedDailyDayKey(chartData);

  if (!previousCloseDayKey || !lastClosedDailyDayKey) {
    return null;
  }

  return previousCloseDayKey === lastClosedDailyDayKey ? previousClose : null;
}

export function computeAssetPriceChange(
  asset: DayChangeAsset | null,
  period: string,
  currentPrice: number | null,
  chartData: AssetChart | null,
  referenceChartData: AssetChart | null = null
): number {
  if (period === "1d" && currentPrice !== null && asset) {
    const previousClose = getValidatedPreviousClose(asset, referenceChartData);
    if (previousClose !== null) {
      return ((currentPrice - previousClose) / previousClose) * 100;
    }
  }

  if (chartData?.price_points && chartData.price_points.length >= 2) {
    const first = chartData.price_points[0].price;
    const last = chartData.price_points[chartData.price_points.length - 1].price;
    return ((last - first) / first) * 100;
  }

  return 0;
}

export function filterChartToLatestIntradaySession(chartData: AssetChart | null): AssetChart | null {
  if (!chartData || chartData.price_points.length === 0) {
    return chartData;
  }

  const points = chartData.price_points;
  const maxSessionGapMs = 6 * 60 * 60 * 1000;
  let startIndex = points.length - 1;

  for (let index = points.length - 1; index > 0; index -= 1) {
    const currentTimestamp = toTimestampMs(points[index].timestamp);
    const previousTimestamp = toTimestampMs(points[index - 1].timestamp);
    if (currentTimestamp === null || previousTimestamp === null) {
      break;
    }

    const gapMs = currentTimestamp - previousTimestamp;
    if (gapMs <= 0 || gapMs > maxSessionGapMs) {
      break;
    }

    startIndex = index - 1;
  }

  return {
    ...chartData,
    price_points: points.slice(startIndex),
  };
}
